import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import './UserAccessPage.css';
import HomeButton from './HomeButton';

const CHAT_API_BASE = '/api/chat';

const authFetch = async (url, opts = {}) => {
  const token = await api.ensureAuthenticated();
  return fetch(url, { ...opts, headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` } });
};

// Admin-only screen: set each user's page access (a denylist of top-level pages
// and project sub-tabs) and project scope (all vs. only assigned projects),
// plus manage which projects an 'assigned'-scope user belongs to.
const UserAccessPage = ({ onBack, currentUser }) => {
  const adminId = currentUser?.id;

  const [catalog, setCatalog] = useState({ top_level: [], project_tabs: [] });
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const projectName = useCallback(
    (id) => (projects.find((p) => p.id === id)?.name) || '(project)',
    [projects]
  );

  useEffect(() => {
    if (!adminId) return;
    (async () => {
      try {
        const [cRes, uRes, pRes] = await Promise.all([
          authFetch(`${CHAT_API_BASE}/user-access.php?action=catalog`),
          authFetch(`${CHAT_API_BASE}/user-access.php?action=list&acting_user_id=${encodeURIComponent(adminId)}`),
          authFetch(`${CHAT_API_BASE}/project-list.php`),
        ]);
        const c = await cRes.json().catch(() => ({}));
        const u = await uRes.json().catch(() => ({}));
        const p = await pRes.json().catch(() => ({}));
        if (c.success && c.catalog) setCatalog(c.catalog);
        if (u.success) setUsers(u.data || []);
        setProjects(Array.isArray(p) ? p : (p.data || []));
      } catch (e) {
        setMsg({ type: 'error', text: 'Failed to load user access data.' });
      }
      setLoading(false);
    })();
  }, [adminId]);

  const selectUser = async (uid) => {
    setSelectedUserId(uid);
    setMsg(null);
    setCfg(null);
    try {
      const res = await authFetch(
        `${CHAT_API_BASE}/user-access.php?action=get&acting_user_id=${encodeURIComponent(adminId)}&target_user_id=${encodeURIComponent(uid)}`
      );
      const data = await res.json().catch(() => ({}));
      if (data.success && data.data) {
        setCfg({
          project_scope: data.data.project_scope === 'assigned' ? 'assigned' : 'all',
          denied: new Set(data.data.denied_pages || []),
          member_project_ids: data.data.member_project_ids || [],
          is_admin: !!data.data.is_admin,
        });
      } else {
        setMsg({ type: 'error', text: data.message || 'Failed to load this user.' });
      }
    } catch (e) {
      setMsg({ type: 'error', text: 'Failed to load this user.' });
    }
  };

  const togglePage = (key) =>
    setCfg((c) => {
      const d = new Set(c.denied);
      if (d.has(key)) d.delete(key); else d.add(key);
      return { ...c, denied: d };
    });

  const setScope = (scope) => setCfg((c) => ({ ...c, project_scope: scope }));

  const save = async () => {
    if (!cfg || !selectedUserId) return;
    setSaving(true);
    setMsg(null);
    try {
      const res = await authFetch(`${CHAT_API_BASE}/user-access.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          acting_user_id: adminId,
          user_id: selectedUserId,
          project_scope: cfg.project_scope,
          denied_pages: [...cfg.denied],
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setMsg({ type: 'success', text: 'Access saved.' });
        setUsers((us) => us.map((u) => (u.id === selectedUserId
          ? { ...u, project_scope: cfg.project_scope, denied_pages: [...cfg.denied] }
          : u)));
      } else {
        setMsg({ type: 'error', text: data.message || 'Save failed.' });
      }
    } catch (e) {
      setMsg({ type: 'error', text: 'Save failed.' });
    }
    setSaving(false);
  };

  const addMember = async (projectId) => {
    if (!projectId) return;
    try {
      await authFetch(`${CHAT_API_BASE}/project-members.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ acting_user_id: adminId, project_id: projectId, user_id: selectedUserId }),
      });
      setCfg((c) => ({ ...c, member_project_ids: [...new Set([...c.member_project_ids, projectId])] }));
    } catch (e) {
      setMsg({ type: 'error', text: 'Failed to add project.' });
    }
  };

  const removeMember = async (projectId) => {
    try {
      await authFetch(
        `${CHAT_API_BASE}/project-members.php?acting_user_id=${encodeURIComponent(adminId)}&project_id=${encodeURIComponent(projectId)}&user_id=${encodeURIComponent(selectedUserId)}`,
        { method: 'DELETE' }
      );
      setCfg((c) => ({ ...c, member_project_ids: c.member_project_ids.filter((id) => id !== projectId) }));
    } catch (e) {
      setMsg({ type: 'error', text: 'Failed to remove project.' });
    }
  };

  const selectedUser = users.find((u) => u.id === selectedUserId);
  const availableProjects = projects.filter((p) => !(cfg?.member_project_ids || []).includes(p.id));

  return (
    <div className="ua-page">
      <div className="ua-header">
        <HomeButton onClick={onBack} />
        <button className="ua-back" onClick={onBack}>← Back</button>
        <h1>User Access</h1>
        <p className="ua-sub">Restrict which pages a user can open and which projects they can see. Admins always have full access.</p>
      </div>

      {loading ? (
        <div className="ua-loading">Loading…</div>
      ) : (
        <div className="ua-body">
          <div className="ua-userlist">
            {users.length === 0 && <p className="ua-empty">No users found.</p>}
            {users.map((u) => {
              const restricted = u.project_scope === 'assigned' || (u.denied_pages && u.denied_pages.length > 0);
              return (
                <button
                  key={u.id}
                  className={`ua-user ${u.id === selectedUserId ? 'active' : ''}`}
                  onClick={() => selectUser(u.id)}
                >
                  <span className="ua-user-name">{u.name}</span>
                  {u.is_admin
                    ? <span className="ua-tag ua-tag-admin">Admin</span>
                    : restricted
                      ? <span className="ua-tag ua-tag-restricted">Restricted</span>
                      : <span className="ua-tag ua-tag-full">Full access</span>}
                </button>
              );
            })}
          </div>

          <div className="ua-editor">
            {!cfg ? (
              <p className="ua-hint">Select a user to configure their access.</p>
            ) : cfg.is_admin ? (
              <div className="ua-admin-note">
                <h2>{selectedUser?.name}</h2>
                <p>This user is an administrator and always has full access to every page and project. Access restrictions don’t apply to admins.</p>
              </div>
            ) : (
              <>
                <h2>{selectedUser?.name}</h2>

                <section className="ua-section">
                  <h3>Project access</h3>
                  <label className="ua-radio">
                    <input type="radio" name="scope" checked={cfg.project_scope === 'all'} onChange={() => setScope('all')} />
                    All projects
                  </label>
                  <label className="ua-radio">
                    <input type="radio" name="scope" checked={cfg.project_scope === 'assigned'} onChange={() => setScope('assigned')} />
                    Only projects they’re assigned to
                  </label>

                  {cfg.project_scope === 'assigned' && (
                    <div className="ua-members">
                      <div className="ua-members-label">Assigned projects</div>
                      {cfg.member_project_ids.length === 0 && (
                        <p className="ua-members-empty">No projects yet — add one below. (Projects they create are added automatically.)</p>
                      )}
                      <div className="ua-chips">
                        {cfg.member_project_ids.map((pid) => (
                          <span key={pid} className="ua-chip">
                            {projectName(pid)}
                            <button className="ua-chip-x" title="Remove" onClick={() => removeMember(pid)}>×</button>
                          </span>
                        ))}
                      </div>
                      <select className="ua-add-project" value="" onChange={(e) => addMember(e.target.value)}>
                        <option value="">+ Add a project…</option>
                        {availableProjects.map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </section>

                <section className="ua-section">
                  <h3>Page access</h3>
                  <p className="ua-section-hint">Unchecked pages are hidden and blocked for this user.</p>

                  <div className="ua-group-title">Main pages</div>
                  <div className="ua-checks">
                    {catalog.top_level.map((pg) => (
                      <label key={pg.key} className="ua-check">
                        <input type="checkbox" checked={!cfg.denied.has(pg.key)} onChange={() => togglePage(pg.key)} />
                        {pg.label}
                      </label>
                    ))}
                  </div>

                  <div className="ua-group-title">Within a project</div>
                  <div className="ua-checks">
                    {catalog.project_tabs.map((pg) => (
                      <label key={pg.key} className="ua-check">
                        <input type="checkbox" checked={!cfg.denied.has(pg.key)} onChange={() => togglePage(pg.key)} />
                        {pg.label}
                      </label>
                    ))}
                  </div>
                </section>

                <div className="ua-actions">
                  <button className="ua-save" onClick={save} disabled={saving}>
                    {saving ? 'Saving…' : 'Save access'}
                  </button>
                  {msg && <span className={`ua-msg ua-msg-${msg.type}`}>{msg.text}</span>}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAccessPage;
